import React from 'react'
import PropTypes from 'prop-types'
import { Card, Separator } from '@fs/zion-ui'
import EssentialInformationReview from './components/EssentialInformationReview'
import EssentialInformationForm from './components/EssentialInformationForm'
import { FullTextApp } from '../../../../shared/services/config'
import EssentialInformationHeader from './components/EssentialInformationHeader'

/**
 * Type definition for the onSave callback function.
 * @callback OnSaveCallback
 * @param {Object} data - The form data submitted from the EssentialInformationCard.
 */

/**
 * EssentialInformationCard component displays and manages a form for creating, editing or reviewing
 * essential information about a person. It integrates with sls and highlight contexts,
 * and supports both edit and review modes.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.person - The person object containing essential information to display or edit. Usually a SLS person object.
 * @param {OnSaveCallback} [props.onSave=(data) => {}] - Callback function invoked when the form is submitted.
 * @param {boolean} [props.reviewed=false] - If true, displays the review mode; otherwise, edit mode.
 * @param {Function} [props.setReviewed] - Setter to update reviewed state (used in edit mode).
 * @param {boolean}  [props.editMode=true]     Forces the card into edit mode (wizard / step-based flows).
 * @param {Function} [props.setEditMode] - Setter to update edit mode state.
 * @param {boolean} [props.saveCardDataToSls=true] - Whether to save changes to SLS backend.
 * @param {string} [props.appName=R2AppName] - Application name for attribution.
 * @param {boolean} [props.isPrimaryPerson=person?.primary || false] - Indicates if the person is the primary person.
 * @param {Function} [props.onDirtyChange=(isDirty) => {}] - Callback function invoked when the form dirty state changes.
 * @param {string}   [props.splitScreenPartitionKey]        Split screen partition key if using split screen.
 * @returns {JSX.Element} The rendered EssentialInformationCard component.
 */
const EssentialInformationCard = ({
  person,
  onSave,
  reviewed = false,
  editMode = true,
  setEditMode = () => {},
  setReviewed = () => {},
  saveCardDataToSls = false,
  appName = FullTextApp,
  isPrimaryPerson = person?.primary ?? false,
  onDirtyChange = () => {},
  splitScreenPartitionKey,
  disableEdit = false,
}) => {
  return (
    <Card margins="xxs" edges={editMode ? 'elevation' : 'border'}>
      <EssentialInformationHeader
        isEditing={editMode}
        setIsEditing={setEditMode}
        reviewed={reviewed}
        disableEdit={disableEdit}
      />
      <Separator size="xs" />
      {!editMode ? (
        <EssentialInformationReview person={person} />
      ) : (
        <EssentialInformationForm
          person={person}
          saveCardDataToSls={saveCardDataToSls}
          onSave={onSave}
          appName={appName}
          isPrimaryPerson={isPrimaryPerson}
          reviewed={reviewed}
          setReviewed={setReviewed}
          onDirtyChange={onDirtyChange}
          setIsEditing={setEditMode}
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
      )}
    </Card>
  )
}

EssentialInformationCard.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string,
    primary: PropTypes.bool,
  }).isRequired,
  onSave: PropTypes.func,
  editMode: PropTypes.bool,
  reviewed: PropTypes.bool,
  setEditMode: PropTypes.func,
  setReviewed: PropTypes.func,
  saveCardDataToSls: PropTypes.bool,
  appName: PropTypes.string,
  isPrimaryPerson: PropTypes.bool,
  onDirtyChange: PropTypes.func,
  splitScreenPartitionKey: PropTypes.string,
}

export default EssentialInformationCard
